import { useCallback, useMemo, useState } from 'react';

import useAdjustmentCatalog from './use.adjustment.catalog';
import useAdjustmentActions from './use.adjustment.actions';
import { buildPreview, describeSelection, defaultPieceOption, findPieceOption } from '../../../../domain/services/solicitation/adjustment.preview.model';
import { MINIMUM_REASON_LENGTH, buildBatchKey } from './adjustment.catalog.constants';

const EMPTY_FORM = { selection: [], reason: '', batchKey: buildBatchKey() };

const nextEntryId = (selection) => selection.reduce((highest, entry) => Math.max(highest, entry.entryId), 0) + 1;

const entryFor = (item, selection, pieceOptions) => {
    const option = item.requiresServiceChoice ? defaultPieceOption(pieceOptions) : null;

    return {
        entryId: nextEntryId(selection),
        catalogItemId: item.id,
        serviceId: option ? option.serviceId : null,
        calculationServiceId: option ? option.calculationServiceId : null
    };
}

const groupsOf = (catalog) => catalog.reduce((grupos, item) => grupos.some(grupo => grupo.key === item.groupKey)
    ? grupos
    : grupos.concat({ key: item.groupKey, label: item.groupLabel || item.groupKey }), []);

const withPreview = (selection, changes) => selection.map((entry, index) => ({ ...entry, preview: changes[index] || {} }));

const loadedView = (loaded) => ({
    isLoading: loaded.isLoading,
    catalog: loaded.catalog,
    pieceOptions: loaded.pieceOptions,
    applied: loaded.applied,
    selectedGroup: loaded.selectedGroup,
    groups: groupsOf(loaded.catalog),
    itemsOfGroup: loaded.catalog.filter(item => item.groupKey === loaded.selectedGroup)
});

const acceptsManyEntries = (item) => item.acceptsMany === true;

const isAlreadySelected = (selection, item) =>
    !acceptsManyEntries(item) && selection.some(entry => entry.catalogItemId === item.id);

const submissionOf = (reason, preview, batchKey) => {
    const typed = reason.trim();

    return {
        batchKey,
        reason: typed || describeSelection(preview.changes),
        reasonTypedByUser: Boolean(typed)
    };
}

const buildSelectionActions = (selection, pieceOptions, patchForm) => ({
    toggle: (item) => patchForm({
        selection: isAlreadySelected(selection, item)
            ? selection.filter(entry => entry.catalogItemId !== item.id)
            : selection.concat(entryFor(item, selection, pieceOptions))
    }),
    removeEntry: (entryId) => patchForm({ selection: selection.filter(entry => entry.entryId !== entryId) }),
    changePiece: (target, choice) => patchForm({
        selection: selection.map(entry => entry.entryId === target.entryId ? { ...entry, ...choice } : entry)
    })
});

export { acceptsManyEntries };

export default function useAdjustmentSheet({ isOpen, priceInfo, onClose, onApplied, setAlert, role }) {

    const { requestId, responsibleId, responsibleName, amount, customerAmount, priceFees } = priceInfo;

    const [form, setForm] = useState(EMPTY_FORM);
    const patchForm = (patch) => setForm(current => ({ ...current, ...patch }));
    const resetForm = useCallback(() => setForm({ selection: [], reason: '', batchKey: buildBatchKey() }), []);

    const loaded = useAdjustmentCatalog({ isOpen, requestId, responsibleId, onLoadStart: resetForm });
    const actions = useAdjustmentActions({ requestId, responsibleId, responsibleName, onClose, onApplied, setAlert });

    const { selection, reason, batchKey } = form;

    const preview = useMemo(() => buildPreview({
        catalog: loaded.catalog, pieceOptions: loaded.pieceOptions, selection,
        faciliterAmount: amount, customerAmount, role, priceFees
    }), [loaded.catalog, loaded.pieceOptions, selection, amount, customerAmount, role, priceFees]);

    const isReasonValid = !preview.requiresReason || reason.trim().length >= MINIMUM_REASON_LENGTH;
    const selectionActions = buildSelectionActions(selection, loaded.pieceOptions, patchForm);

    return {
        ...loadedView(loaded),
        ...selectionActions,
        error: actions.actionError || loaded.loadError,
        isSaving: actions.isSaving,
        reason, preview,
        selection: withPreview(selection, preview.changes),
        optionOf: (entry) => findPieceOption(loaded.pieceOptions, entry),
        setReason: (value) => patchForm({ reason: value }),
        selectGroup: (group) => loaded.setSelectedGroup(group),
        discard: resetForm,
        canApply: preview.hasChanges && !preview.blocked && !preview.neutralizedByReplacement && isReasonValid && !actions.isSaving,
        apply: () => actions.apply(selection, submissionOf(reason, preview, batchKey)),
        revert: (adjustment) => actions.revert(adjustment)
    };
}
